import { css } from '@emotion/react';
import { CSSProperties } from 'react';
import { colors, semantic_colors, shapes } from '../../foundation';
import { getAutoColorCode } from '../../styles';
import { getInputSpacingBySize } from '../input/styles';
import { InputSize } from '../input/types';

export const date_picker_base_css = css`
  position: relative;
  user-select: none;
  background-color: ${semantic_colors.background.surface};
`;

export const date_picker_compact_content_css = css`
  padding: 20px;
  border-right: 1px solid ${semantic_colors.border.primary};
`;

export const date_picker_compact_right_css = css`
  padding: 8px;
  display: flex;
  align-items: stretch;
`;

export const date_picker_compact_footer_css = css`
  width: 100%;
  padding: 12px;
  border-top: 1px solid ${semantic_colors.border.primary};
`;

export const popover_picker_css = css`
  ${shapes.shadow.low};
  ${shapes.border_radius.small};
`;

export const bottom_sheet_picker_css = css`
  width: 100%;
  padding: 12px 16px 24px;
  flex: 1 1 auto;
  overflow-x: hidden;
`;

export const caption_text_button_css = css`
  padding: 0 2px;
  transition: all 0.1s linear;
  background-color: ${semantic_colors.background.surface};

  &:disabled {
    cursor: not-allowed;
  }

  &:hover:not([disabled]) {
    background-color: ${getAutoColorCode(semantic_colors.background.surface).hover};
  }
  &:active:not([disabled]) {
    background-color: ${getAutoColorCode(semantic_colors.background.surface).pressed};
  }
`;
export const caption_arrow_button_css = css`
  white-space: nowrap;
  padding: 0;
  background-color: ${semantic_colors.background.surface};
  border: none;
  cursor: pointer;
  ${shapes.border_radius.xsmall};
  color: ${semantic_colors.content.secondary};
  transition: all 0.1s linear;

  &:hover:not([disabled]) {
    background-color: ${getAutoColorCode(semantic_colors.background.surface).hover};
    color: ${semantic_colors.content.primary};
  }
  &:disabled {
    cursor: not-allowed;
  }
  &:active:not([disabled]) {
    background-color: ${getAutoColorCode(semantic_colors.background.surface).pressed};
    color: ${semantic_colors.content.primary};
  }
  &:focus {
    outline: 3px solid ${semantic_colors.tab_focus};
    box-shadow: none;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const range_input_default_style = {
  'position': 'relative',
  'border': 0,
  'borderRadius': 0,
  'backgroundColor': 'transparent',
  'padding': 0,
  'transition': 'none',
  '&:hover': {
    borderColor: 'transparent',
  },
  '&:focus, &:focus-within': {
    outline: 'none',
    border: 'none',
  },
} as CSSProperties;

export const range_picker_focus_style = css`
  position: relative;
  &:has(.focused)::before {
    position: absolute;
    bottom: 0;
    left: 0;
    content: '';
    height: 3px;
    background: ${semantic_colors.accent.primary};
    margin-right: 18px;
    transition: all 0.23s ease;
  }
  &:has(.error.focused)::before {
    background: ${semantic_colors.state.negative};
  }
`;

export const date_picker_input_entered_disabled_style = css`
  input[disabled] {
    cursor: not-allowed;
    color: ${semantic_colors.content.disabled};
    -webkit-text-fill-color: ${semantic_colors.content.disabled};
  }
`;

export const range_input_disabled_style = css`
  &:hover {
    border: none;
  }
`;

const DATE_PICKER_HORIZONTAL_PADDING_BY_SIZE: { [key in InputSize]: number } = {
  xsmall: 8,
  small: 10,
  medium: 12,
  large: 16,
};

// Date picker small inputs reserve room for calendar/remove icons, so they use a 6px gap in the 32px field.
export const getDatePickerInputSpacingBySize = (size: InputSize) => {
  return size === 'small' ? 6 : getInputSpacingBySize(size);
};

export const getDatePickerInputStyleBySize = (size?: InputSize): CSSProperties | undefined => {
  if (size !== 'small') return undefined;

  return {
    columnGap: getDatePickerInputSpacingBySize(size),
    padding: `0 ${DATE_PICKER_HORIZONTAL_PADDING_BY_SIZE.small}px`,
  };
};

export const getDatePickerInputWrapperStyleBySize = (size: InputSize) => {
  if (size !== 'small') return undefined;

  return css`
    column-gap: ${getDatePickerInputSpacingBySize(size)}px;
    padding: 0 ${DATE_PICKER_HORIZONTAL_PADDING_BY_SIZE.small}px;
  `;
};

export const year_month_select_panel_css = css`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 8px 0;
  color: ${semantic_colors.content.primary};
  border: none;
  background-color: ${semantic_colors.background.surface};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.1s linear;

  &:hover:not([disabled]) {
    background-color: ${getAutoColorCode(semantic_colors.background.surface).hover};
  }
  &:disabled {
    cursor: not-allowed;
    color: ${semantic_colors.content.disabled};
  }
  &:active:not([disabled]) {
    background-color: ${getAutoColorCode(semantic_colors.background.surface).pressed};
  }
  &:focus {
    outline: 3px solid ${semantic_colors.tab_focus};
    box-shadow: none;
    z-index: 1;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &.start,
  &.end {
    color: ${semantic_colors.background.surface};
    background-color: ${semantic_colors.accent.primary};

    &:hover {
      background-color: ${getAutoColorCode(semantic_colors.accent.primary).hover};
    }
    &:active {
      background-color: ${getAutoColorCode(semantic_colors.accent.primary).pressed};
    }
  }
  &.start {
    margin-left: 2px;
    border-radius: 8px 0 0 8px;
  }
  &.end {
    margin-right: 2px;
    border-radius: 0 8px 8px 0;
  }
  &.single {
    margin-left: 0;
    margin-right: 0;
    border-radius: 8px;
  }
  &.middle {
    border-radius: 0;
    color: ${semantic_colors.accent.primary};
    background-color: ${semantic_colors.accent.light};
    &:hover {
      background-color: ${colors.blue100};
    }
    &:active {
      background-color: ${colors.blue200};
    }
  }
`;

export const getRangeFocusInputStyle = (size: InputSize) => {
  const padding = DATE_PICKER_HORIZONTAL_PADDING_BY_SIZE[size];
  const spacing = getDatePickerInputSpacingBySize(size);
  const startIconWidth = 12;
  const endIconWidth = 16;

  return css`
    &:has(.focused.start)::before {
      width: calc((50% - ${padding + spacing / 2}px - ${spacing + startIconWidth}px));
      transform: translateX(${padding}px);
    }
    &:has(.focused.end)::before {
      width: calc((50% - ${padding + spacing / 2}px - ${spacing + endIconWidth}px));
      transform: translateX(calc(100% + ${padding + spacing * 3 + startIconWidth}px));
    }
  `;
};

export const date_ragne_preset_disabled_style = css`
  color: ${semantic_colors.content.disabled};
  cursor: not-allowed;
  &:hover,
  &:active {
    background-color: ${semantic_colors.background.surface};
  }
`;
